import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import CountrySelect from './CountrySelect';
import './CreateEditTeam.css';

const CreateEditTeam = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  // Команду под событие редактируют со страницы события — туда и возвращаемся
  const backPath = searchParams.get('event') ? `/events/${searchParams.get('event')}` : '/teams';

  const [formData, setFormData] = useState({
    name: '',
    country: '',
    description: '',
    avatar: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Команда под событие: страна ей не нужна — выступает в событии, а не за регион
  const [isEventTeam, setIsEventTeam] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchTeam();
    }
  }, [id]);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/teams/${id}`);
      const data = response.data;
      setFormData({
        name: data.name,
        country: data.country || '',
        description: data.description || '',
        avatar: data.avatar || ''
      });
      setIsEventTeam(Boolean(data.event_team));
      if (data.avatar) {
        setAvatarPreview(data.avatar);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({ ...prev, country }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t('profile.avatarTooLarge'));
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return formData.avatar;

    setUploadingAvatar(true);
    const formDataUpload = new FormData();
    formDataUpload.append('avatar', avatarFile);

    try {
      const response = await axios.post('/api/upload/team_avatar', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.path;
    } catch (err) {
      throw new Error(t('profile.avatarUploadError'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Загружаем аватар, если выбран новый
      let avatarPath = formData.avatar;
      if (avatarFile) {
        avatarPath = await uploadAvatar();
      }

      const payload = {
        team: {
          ...formData,
          avatar: avatarPath
        }
      };

      if (isEdit) {
        await axios.put(`/api/teams/${id}`, payload);
      } else {
        await axios.post('/api/teams', payload);
      }

      navigate(backPath);
    } catch (err) {
      const errorMsg = err.response?.data?.errors 
        ? err.response.data.errors.join(', ') 
        : err.response?.data?.error || err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('teams.confirmDelete'))) return;

    try {
      setLoading(true);
      await axios.delete(`/api/teams/${id}`);
      navigate(backPath);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit && !formData.name) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="create-edit-team-container">
      <div className="create-edit-team-card">
        <h1>{isEdit ? t('teams.editTeam') : t('teams.createTeam')}</h1>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="avatar-upload-section">
            <div className="avatar-preview">
              {avatarPreview ? (
                <img src={avatarPreview} alt={t('a11y.teamAvatar')} />
              ) : (
                <div className="avatar-placeholder">
                  {formData.name ? formData.name.charAt(0).toUpperCase() : 'T'}
                </div>
              )}
            </div>
            <div className="avatar-upload-controls">
              <label htmlFor="avatar-upload" className="avatar-upload-label">
                {t('teams.changeAvatar')}
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              {avatarPreview && (
                <button
                  type="button"
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview(null);
                    setFormData(prev => ({ ...prev, avatar: '' }));
                  }}
                  className="remove-avatar-btn"
                >
                  {t('teams.removeAvatar')}
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">{t('teams.name')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              minLength={3}
              maxLength={100}
            />
          </div>

          {!isEventTeam && (
            <div className="form-group">
              <label>{t('teams.country')}</label>
              <CountrySelect
                value={formData.country}
                onChange={handleCountryChange}
                language={language}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">{t('teams.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={5}
              maxLength={1000}
            />
            <small>{formData.description.length}/1000</small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="cancel-btn"
              disabled={loading || uploadingAvatar}
            >
              {t('common.cancel')}
            </button>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                className="delete-btn"
                disabled={loading || uploadingAvatar}
              >
                {t('common.delete')}
              </button>
            )}
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || uploadingAvatar}
            >
              {loading || uploadingAvatar
                ? t('common.saving')
                : isEdit
                ? t('common.save')
                : t('teams.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditTeam;